import { createContext, ReactNode, useContext, useMemo } from 'react'

import { UnlikelyProduct } from '@unlikelystudio/commerce-connector'

import { useGetProduct } from '~/hooks/useGetProduct'
import useLocale from '~/hooks/useLocale'

import { serializeShopifyProductData } from '~/data/serialize-shopify-product-data'
import { StoryblokShopifyData } from '~/data/serialize-storyblok-shopify-data'

export const ShopifyProductContext = createContext<ShopifyProductContextValue>(
  {},
)

export type ShopifyProductProviderProps = {
  children: ReactNode | ReactNode[]
  gid: UnlikelyProduct['id']
  isAdjustable?: boolean
  adjustableOffset?: number
}

export type ShopifyProductContextValue = {
  isLoading?: boolean
} & StoryblokShopifyData

export default function ShopifyProductProvider({
  isAdjustable,
  adjustableOffset,
  gid,
  children,
}: ShopifyProductProviderProps) {
  const locale = useLocale()

  const { data: product, isLoading } = useGetProduct({
    id: gid,
    includeQuantityAvailable: true,
    locale,
  })

  const shopifyProduct = serializeShopifyProductData({
    isAdjustable,
    adjustableOffsetValue: adjustableOffset,
    productVariants: product?.variants,
    productImages: product?.images,
    shopifyProductId: product?.id,
    media: product?.media ?? null,
    locale,
  })

  const processedValue: ShopifyProductContextValue = useMemo(
    () => ({
      ...shopifyProduct,
      isLoading,
    }),
    [isLoading, JSON.stringify(shopifyProduct)],
  )

  return (
    <ShopifyProductContext.Provider value={processedValue}>
      {children}
    </ShopifyProductContext.Provider>
  )
}

export function useShopifyProductContext() {
  return useContext(ShopifyProductContext)
}
